import {
  Component,
  OnInit,
  viewChild,
  inject,
  WritableSignal,
  signal,
  OnDestroy,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  Validators,
  FormGroup,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { take } from 'rxjs/operators';
import {
  ControllerNote,
  ControllerNoteCategory,
  SCADAControlValue,
} from 'src/app/model/controllerNotes/controllerNote';
import { Entry } from 'src/app/model/controllerNotes/entry';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { AuthService } from 'src/app/services/auth.service';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { LocationApiService } from 'src/app/services/api/location-api.service';
import { NoteTypeApiService } from 'src/app/services/api/note-type-api.service';
import {
  categories,
  emptyEntry,
  sortByName,
  sortByProperty,
  SortDirection,
} from 'src/app/utils/utils';
import { EntryFormComponent } from '../entry-form/entry-form.component';
import { ControllerNotesApiService } from 'src/app/services/api/controller-notes-api.service';
import { Location } from 'src/app/model/locations/location';
import { ValvePosition } from 'src/app/model/controllerNotes/valvePosition';
import { UnitStatus } from 'src/app/model/controllerNotes/unitStatus';
import { AOR } from 'src/app/model/locations/aor';
import { Facility } from 'src/app/model/locations/facility';
import { NoteType } from 'src/app/model/crm/note-type';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { FacilityNotesDialogComponent } from 'src/app/locations/components/facility-notes-dialog/facility-notes-dialog.component';
import { FacilityNotepadEntry } from 'src/app/model/locations/facility-notepad-entry';
import { FacilityVisitorLogComponent } from 'src/app/locations/components/facility-visitor-log/facility-visitor-log.component';
import { ActionsDropdownComponent } from '../actions-dropdown/actions-dropdown.component';
import printJS from 'print-js';
import { ControllerNotesBuilderService } from 'src/app/services/builder/controller-notes-builder.service';
import { getUserTimezone } from 'src/app/core/functions/date-functions';
import { getCurrentTimeBasedOnView } from 'src/app/crm/functions/controller-note-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { DeviceService } from 'src/app/services/device.service';
import {
  DELETE_NOTE,
  EDIT_NOTE,
  EMAIL_NOTE,
  PRINT_NOTE,
  VIEW_AUDIT,
} from 'src/app/constants/action-constants';
import { ConfirmationService, SelectItem } from '@openng/optimus-ui/api';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { EmailDialogContainerComponent } from 'src/app/core/containers/email-dialog-container.component';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { Checkbox } from '@openng/optimus-ui/checkbox';
import { Divider } from '@openng/optimus-ui/divider';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { ProgressSpinnerComponent } from '../../../../core/components/progress-spinner.component';
import { Select } from '@openng/optimus-ui/select';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { InputText } from '@openng/optimus-ui/inputtext';
import { Editor } from '@openng/optimus-ui/editor';
import { Toast } from '@openng/optimus-ui/toast';
import { DatePipe } from '@angular/common';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';

type LocationDropdownOption = {
  name: string;
};

@Component({
  selector: 'app-note-form',
  templateUrl: './note-form.component.html',
  styleUrls: ['./note-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ActionsDropdownComponent,
    Checkbox,
    Divider,
    ButtonDirective,
    Tooltip,
    ProgressSpinnerComponent,
    Select,
    DatePicker,
    InputText,
    Editor,
    Toast,
    DatePipe,
    TranslatePipe,
    LogEntryComponent,
  ],
})
export class NoteFormComponent implements OnInit, OnDestroy {
  protected _auth = inject(AuthService);
  protected _noteTypeAPI = inject(NoteTypeApiService);
  protected _locationAPI = inject(LocationApiService);
  protected _facilityAPI = inject(FacilityApiService);
  protected _contactAPI = inject(ContactApiService);
  protected _authApi = inject(AuthApiService);
  protected _controllerNoteApi = inject(ControllerNotesApiService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _translateService = inject(TranslateService);
  protected _dialogService = inject(DialogService);
  protected _fb = inject(FormBuilder);
  protected _controllerNoteBuilder = inject(ControllerNotesBuilderService);
  protected _deviceService = inject(DeviceService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _confirmationService = inject(ConfirmationService);
  protected _config = inject(DynamicDialogConfig);
  protected _tabService = inject(TabService);

  readonly id = input<string>();
  readonly entryUpdatedEvent = output<boolean>();

  readonly actionsDropdownChild =
    viewChild.required<ActionsDropdownComponent>('actions');

  readonly selectedBusinessUnits: WritableSignal<string[] | null> = signal([]);
  readonly facilityNotepadEntries: WritableSignal<FacilityNotepadEntry[]> =
    signal([]);
  readonly formType: WritableSignal<string | null> = signal(null);
  readonly isAorView: WritableSignal<boolean> = signal(false);
  readonly totalAors: WritableSignal<AOR[]> = signal([]);

  readonly facility: WritableSignal<Facility | null> = signal(null);
  readonly facilityId: WritableSignal<string | null> = signal(null);
  readonly controllerNote: WritableSignal<ControllerNote | null> = signal(null);
  readonly selectedAors: WritableSignal<AOR[] | null> = signal(null);
  readonly category: WritableSignal<ControllerNoteCategory | null> =
    signal('M&R');
  readonly SCADAControlValues: WritableSignal<SCADAControlValue[]> = signal([]);

  noteFormRef = inject(DynamicDialogRef);
  ref: DynamicDialogRef<any> | null = null;

  canAddEntries: boolean = false;
  categories: any[];
  noteTypes: NoteType[];
  totalNoteTypes: NoteType[];
  facilities: Facility[];
  facilityIds: any[] = [];
  totalFacilities: Facility[];
  aorFilteredFacilities: Facility[] = [];
  aorHiddenFacilities: Facility[] = [];
  valvePositions: ValvePosition[];
  totalLocations: Location[] = [];
  states: LocationDropdownOption[];
  counties: LocationDropdownOption[];
  townships: LocationDropdownOption[];
  contacts: any[];
  unitStatuses: UnitStatus[];
  selectedAOR: AOR | null = null;
  entries: Entry[] | null = null;
  selectedEntry: Entry = emptyEntry;
  entryFormType: string = 'Create';
  entriesLoading: boolean = false;
  facilitiesLoading: boolean = true;
  unitStatusesLoading: boolean = true;
  valvePositionsLoading: boolean = true;
  selectedFacility: Facility | null = null;
  completedDataLoads: number = 0;

  canEdit: boolean = false;
  canEditOwn: boolean = false;
  canCreate: boolean = false;
  canDelete: boolean = false;
  canDeleteOwn: boolean = false;

  showActiveFacilityNotes: boolean = false;
  showActiveVisitors: boolean = false;
  displayEmailDialog: boolean = false;
  noteFormGroup: FormGroup;
  isLoading: boolean = false;
  availableActions: SelectItem[] = [];
  timezone: string | null = null;
  isEditing: boolean = false;

  tabClosingSub?: Subscription;

  constructor() {
    this.categories = categories;
    this.facilities = [];
    this.states = [];
    this.townships = [];
    this.counties = [];
    this.totalNoteTypes = [];
    this.totalFacilities = [];
    this.noteTypes = [];
    this.contacts = [];
    this.unitStatuses = [];
    this.valvePositions = [];

    this.noteFormGroup = this._fb.group({
      typeAOR: this._fb.group({
        category: [null, Validators.required],
        noteType: [null, Validators.required],
        createdAt: [
          {
            value: getCurrentTimeBasedOnView(
              this.selectedAOR?.name as string,
              this.isAorView()
            ),
          },
          Validators.required,
        ],
        aor: [{ value: null }, Validators.required],
        maop: [false],
        falseAlarm: [false],
        abnormalOperation: [false],
        includeInTurnover: [false],
        calloutInitiated: [false],
        maintenance: [false],
      }),
      contactLocInfo: this._fb.group({
        contact: [null],
        contactPhone: [null],
        facility: [null],
        facilityId: [null],
        facilityScadaId: [null],
        state: [null],
        county: [null],
        township: [null],
        callerFirstName: [null],
        callerLastName: [null],
        pipelineSection: [null],
        pipelineNum: [null],
        systemName: [null],
        unit: [null],
        unitStatus: [null],
        valvePosition: [null],
        SCADAControl: [null],
      }),
      initialNoteEntry: [null],
    });

    this.isLoading = true;
    this._locationAPI.getLocations().subscribe({
      next: (locations: Location[]): any => {
        this.totalLocations = locations;
        this.filterLocations();
        this.completedDataLoads += 1;
        this.checkFormReady();
      },
      error: (error) => console.error(error),
    });

    this._contactAPI
      .getContacts()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.contacts = response;
          if (this.contacts && this.contacts.length > 0) {
            this.contacts.forEach((contact) => {
              if (contact.nickName) {
                contact.dropdownName = `${contact.firstName} "${contact.nickName}" ${contact.lastName}`;
              } else {
                contact.dropdownName = `${contact.firstName} ${contact.lastName}`;
              }
            });
            this.contacts.sort((a, b) => sortByProperty(a, b, 'dropdownName'));
          }
          this.completedDataLoads += 1;
          this.checkFormReady();
        },
        error: (error) => console.error(error),
      });

    this.getSCADAControlValues();
    this.prepareForm();
    this.isLoading = false;
  }

  ngOnInit(): void {
    this.canEdit = this._authApi.doesUserHaveClaim(
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES.EDIT_CONTROLLER_NOTE_ENTRIES
    );
    this.canEditOwn = this._authApi.doesUserHaveClaim(
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.EDIT_OWN_CONTROLLER_NOTES
    );
    this.canCreate = this._authApi.doesUserHaveClaim(
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES.ADD_CONTROLLER_NOTE_ENTRIES
    );
    this.canDelete = this._authApi.doesUserHaveClaim(
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.DELETE_CONTROLLER_NOTES
    );
    this.canDeleteOwn = this._authApi.doesUserHaveClaim(
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.DELETE_OWN_CONTROLLER_NOTES
    );

    this.isLoading = true;
    this.timezone = getUserTimezone();
    if (this.id()) {
      this.setupTabClosingSubscription();
      this.setUpReadOnlyNoteView();
    } else {
      this.setUpNoteForm();
    }
  }

  setupTabClosingSubscription(): void {
    this.tabClosingSub = this._tabService.attemptCloseEvent.subscribe(
      (closingTab) => {
        this._tabService.closeTab(closingTab.index);
      }
    );
  }

  setUpReadOnlyNoteView(): void {
    this.formType.set('read-only');
    this.fillActionsDropdown();
    this.disableForm();
    this.getControllerNote();
  }

  setUpNoteForm(): void {
    this.isAorView.set(this._config?.data?.input?.isAorView);
    this.formType.set(this._config?.data?.input?.formType);
    this.selectedBusinessUnits.set(
      this._config?.data?.input?.selectedBusinessUnits
    );
    this.totalAors.set(this._config?.data?.input?.totalAors);
    this.selectedAors.set(this._config?.data?.input?.selectedAors);
    this.controllerNote.set(this._config?.data?.input?.controllerNote);
    this.category.set(this._config?.data?.input?.category);

    this.loadUnitStatuses(this.selectedAOR?.id ?? null);
    this.loadValvePositions(this.selectedAOR?.id ?? null);
    this.checkFormReady();

    if (this.selectedAors()!.length > 0) {
      this.selectedAOR = this.selectedAors()![0];
    }

    if (this.formType() === 'Create') {
      this.isEditing = true;
    }

    this._noteTypeAPI.getNoteTypes().subscribe({
      next: (noteTypes: NoteType[]) => {
        this.totalNoteTypes = noteTypes;
        this.totalNoteTypes.sort(sortByName);
        this.completedDataLoads += 1;
        this.checkFormReady();
      },
      error: (error) => console.error(error),
    });

    this.loadFacilities();
    this.fillActionsDropdown();

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .ADD_CONTROLLER_NOTE_ENTRIES,
      ])
    ) {
      this.canAddEntries = true;
    }
  }

  aorChanged() {
    this.noteFormGroup.patchValue({
      contactLocInfo: {
        facility: null,
        facilityId: null,
        facilityScadaId: null,
      },
    });
    this.loadFacilities();
    this.updateNoteTypes();
  }

  loadFacilities() {
    let aorToFilterOn: string = '';

    if (this.isAorView()) {
      aorToFilterOn =
        this.noteFormGroup.get('typeAOR')?.get('aor')?.value?.name ??
        this.selectedAOR?.name;
    } else {
      aorToFilterOn =
        this.noteFormGroup.get('typeAOR')?.get('aor')?.value?.name ??
        this.controllerNote()?.AORName ??
        '';
    }

    this._facilityAPI
      .getFacilities()
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (data.length === 0) {
            setTimeout(() => {
              this.loadFacilities();
            }, 1000);
            return;
          }
          this.facilities = [...data];
          this.facilities.sort((a, b) => sortByProperty(a, b, 'facilityName'));
          // filter out pipelines and other Facility Types not needed for controller notes
          const filterOutTypes = [
            'Crossroads Pipelines',
            'All Pipeline Facilities',
            'Public Affairs',
            'Columbia Gas Pipeline',
            'Columbia Gulf Pipeline',
            'Columbia Midstream Pipeline',
            'Millenium Pipeline',
            'PNGTS Pipeline',
            'Office',
            'Environmental',
            'Land',
            'Pipeline',
          ];
          this.facilities = this.facilities.filter(
            (facility) => !filterOutTypes.includes(facility.facilityType!)
          );

          this.facilityIds = [...this.facilities];
          this.facilityIds = this.facilityIds.sort((a, b) =>
            sortByProperty(a, b, 'facilityUid')
          );

          this.totalFacilities = this.facilities;
          if (aorToFilterOn !== '') {
            this.aorFilteredFacilities = this.totalFacilities.filter(
              (facility) =>
                !facility.hidden &&
                facility.AORs.map((aor) => aor.name).includes(aorToFilterOn)
            );
            this.aorHiddenFacilities = this.totalFacilities.filter(
              (facility) =>
                facility.hidden &&
                facility.AORs.map((aor) => aor.name).includes(aorToFilterOn)
            );
          } else {
            this.aorFilteredFacilities = this.totalFacilities.filter(
              (facility) => facility.hidden == false
            );
          }

          this.facilitiesLoading = false;
          this.onFacilitiesLoaded();
          this.filterFacilities();
        },
        error: (error) => console.error(error),
      });
  }

  loadUnitStatuses(aorId: string | null) {
    this._controllerNoteApi.getUnitStatuses(aorId).subscribe({
      next: (unitStatuses: UnitStatus[]): any => {
        this.unitStatuses = unitStatuses.sort((a, b) =>
          sortByProperty(a, b, 'name')
        );
        this.unitStatusesLoading = false;
        this.completedDataLoads += 1;
        this.checkFormReady();
      },
      error: (error) => console.error(error),
    });
  }

  loadValvePositions(aorId: string | null) {
    this._controllerNoteApi.getValvePositions(aorId).subscribe({
      next: (valvePositions: ValvePosition[]): any => {
        this.valvePositions = valvePositions.sort((a, b) =>
          sortByProperty(a, b, 'name')
        );
        this.valvePositionsLoading = false;
        this.completedDataLoads += 1;
        this.checkFormReady();
      },
      error: (error) => console.error(error),
    });
  }

  addEntry() {
    if (!this.canAddEntries) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.ERROR.CREATE_NOTE_ENTRY',
        bodyKey: 'CRM.MESSAGES.ERROR.MISSING_PERMISSIONS',
      });
      return;
    }
    this.selectedEntry = emptyEntry;
    this.openEntryForm(this.selectedEntry, 'Create');
  }

  async openEntryForm(entry: Entry, formType: string): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get('CRM.MESSAGES.HEADERS.NOTE_ENTRY')
    );

    this.ref = this._dialogService.open(EntryFormComponent, {
      header: dialogHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      width: '38%',
      focusOnShow: false,
      data: {
        entry: entry,
        formType: formType,
        minDateTime: this.controllerNote()?.createdAt,
        aor: this.controllerNote()?.AORName,
        isAorView: this.isAorView(),
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If there's no data provided in the onclose, we didn't actually do anything to the entry
        // so we don't need to update the controller note.
        return;
      }

      if (data.entryFormType === 'Edit') {
        this.updateControllerNoteEntry(data);
      }
      if (data.entryFormType === 'Create') {
        this.createControllerNoteEntry(data);
      }
    });
  }

  onSubmit() {
    if (!this.noteFormGroup.valid) {
      // failsafe to prevent inspect element from making the submit button enabled
      console.log('Form was somehow submitted without being valid!');
      return;
    }

    if (this.formType() === 'Create') {
      this.createControllerNote(this.noteFormGroup);
    } else if (
      this.formType() === 'Edit' &&
      (this.canEdit || (this.canEditOwn && this.isOwnNote()))
    ) {
      this.updateControllerNote(this.noteFormGroup);
    } else {
      // Failsafe, if we somehow didn't do either of the above actions we should set the loading state to false and show an error
      this.isLoading = false;
      this._logAndMessage.warn(
        'No Action taken! No note was created or edited!'
      );
    }
  }

  createControllerNote(input: any) {
    this.isLoading = true;
    let noteInput = this._controllerNoteBuilder.buildControllerNoteInput(
      this.controllerNote()!,
      input,
      this.isAorView()
    );

    this._controllerNoteApi
      .createControllerNote(noteInput, false)
      .pipe(take(1))
      .subscribe({
        next: (newControllerNote) => {
          this.noteFormRef?.close(newControllerNote);
          this.isLoading = false;
          this._logAndMessage.translateToSuccessMessage({
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            bodyKey: 'CRM.MESSAGES.SUCCESS.CREATE_CONTROLLER_NOTE',
          });
        },
        error: (error) => {
          this.isLoading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CRM.MESSAGES.ERROR.CREATE_CONTROLLER_NOTE',
          });
        },
      });
  }

  updateControllerNote(input: any) {
    this.isLoading = true;
    let noteInput = this._controllerNoteBuilder.buildControllerNoteInput(
      this.controllerNote()!,
      input
    );

    this._controllerNoteApi.updateControllerNote(noteInput).subscribe({
      next: (editedControllerNote) => {
        this.isLoading = false;
        this.ref?.close(editedControllerNote);
      },
      error: (error) => {
        this.isLoading = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CRM.MESSAGES.ERROR.EDIT_CONTROLLER_NOTE',
        });
      },
    });
  }

  createControllerNoteEntry(input: any) {
    if (!this.canAddEntries) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.ERROR.CREATE_NOTE_ENTRY',
        bodyKey: 'CRM.MESSAGES.ERROR.MISSING_PERMISSIONS',
      });
      return;
    }
    let newEntry = this._controllerNoteBuilder.buildNoteEntryInput(
      this.controllerNote()!,
      this.isAorView(),
      input
    );
    this.entriesLoading = true;

    this._controllerNoteApi
      .createNoteEntry(
        newEntry,
        this.isAorView(),
        this.controllerNote()?.AORName!
      )
      .subscribe({
        next: (createdEntry) => {
          this.entries?.push(createdEntry);
          this.entries?.sort((a: Entry, b: Entry) =>
            sortByProperty(a, b, 'time', SortDirection.DESCENDING)
          );
          this.entriesLoading = false;
        },
        error: (error) => {
          this.entriesLoading = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CRM.MESSAGES.ERROR.CREATE_NOTE_ENTRY',
          });
        },
      });
  }

  updateControllerNoteEntry(input: any) {
    if (!this.canEditEntry(input)) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.ERROR.EDIT_NOTE_ENTRY',
        bodyKey: 'CRM.MESSAGES.ERROR.MISSING_PERMISSIONS',
      });
      return;
    }

    let newEntry = this._controllerNoteBuilder.buildNoteEntryInput(
      this.controllerNote()!,
      this.isAorView(),
      input
    );

    this._controllerNoteApi
      .updateNoteEntry(
        newEntry,
        this.isAorView(),
        this.controllerNote()?.AORName!
      )
      .pipe(take(1))
      .subscribe({
        next: (editedEntry) => {
          let oldIndex = this.entries!.findIndex((x) => x.id === newEntry.id);
          this.entries?.splice(oldIndex, 1, editedEntry);
          this.entries?.sort((a: Entry, b: Entry) =>
            sortByProperty(a, b, 'time', SortDirection.DESCENDING)
          );
        },
        error: (error) => {
          error = error.message.replace('GraphQL error: ', '');
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CRM.MESSAGES.ERROR.EDIT_NOTE_ENTRY',
          });
        },
      });
  }

  updateFields() {
    if (this.category()) {
      this.noteFormGroup.patchValue({
        typeAOR: {
          category: this.categories.find(
            (category) => category.name == this.category()
          ),
        },
      });
    }
    if (this.formType() === 'Create') {
      this.noteFormGroup.patchValue({
        typeAOR: {
          aor: this.totalAors().find(
            (aor) => aor.name === this.selectedAOR?.name
          ),
          createdAt: getCurrentTimeBasedOnView(
            this.selectedAOR?.name as string,
            this.isAorView()
          ),
        },
      });
    }
    if (this.formType() === 'Edit') {
      this.updateNoteTypes();
      this.entries = this.controllerNote()?.entries!;
      this.noteFormGroup.patchValue({
        typeAOR: {
          category: this.categories.find(
            (category) => category.name === this.controllerNote()?.category
          ),
          noteType: this.noteTypes.find(
            (type) => type.name === this.controllerNote()?.noteType
          ),
          createdAt: this.controllerNote()?.createdAt,
          aor: this.totalAors().find(
            (aor) => aor.name === this.controllerNote()?.AORName
          ) ?? [
            {
              label: this.controllerNote()?.AORName,
              value: this.controllerNote()?.AORId,
            },
          ],
          maop: this.controllerNote()?.maop,
          falseAlarm: this.controllerNote()?.falseAlarm,
          abnormalOperation: this.controllerNote()?.abnormalOperation,
          includeInTurnover: this.controllerNote()?.addToTurnover,
          calloutInitiated: this.controllerNote()?.calloutInitiated,
          maintenance: this.controllerNote()?.maintenance,
        },
        contactLocInfo: {
          contact: this.contacts.find(
            (contact) => contact.id === this.controllerNote()?.contactId
          ),
          contactPhone: this.controllerNote()?.phoneNumber,
          callerFirstName: this.controllerNote()?.callerFirstName,
          callerLastName: this.controllerNote()?.callerLastName,
          facility: this.facilities.find(
            (facility) =>
              facility.facilityName === this.controllerNote()?.facilityName
          ),
          facilityId: this.facilities.find(
            (facility) =>
              facility.facilityUid === this.controllerNote()?.facilityUID
          ),
          facilityScadaId: this.facilities.find(
            (facility) =>
              facility.facilityUid === this.controllerNote()?.facilityUID
          ),
          state: this.states.find(
            (state) => state.name === this.controllerNote()?.state
          ),
          county: this.counties.find(
            (county) => county.name === this.controllerNote()?.county
          ),
          township: this.townships.find(
            (township) => township.name === this.controllerNote()?.township
          ),
          pipelineSection: this.controllerNote()?.pipelineSection,
          pipelineNum: this.controllerNote()?.pipeline,
          systemName: this.controllerNote()?.system,
          unit: this.controllerNote()?.unit,
          unitStatus: this.unitStatuses.find(
            (unitStatus) =>
              unitStatus.name === this.controllerNote()?.unitStatus?.name
          ),
          valvePosition: this.valvePositions.find(
            (valvePosition) =>
              valvePosition.name === this.controllerNote()?.valvePosition?.name
          ),
          SCADAControl: this.controllerNote()?.SCADAControl?.id,
        },
      });
      if (this.controllerNote()?.facilityId) {
        this.checkFacilityNotes(this.controllerNote()?.facilityId);
        this.checkForVisitors(this.controllerNote()?.facilityId!);
      }
    }
    if (this.formType() === 'read-only') {
      this.entries = this.controllerNote()?.entries!;
      this.noteFormGroup.patchValue({
        typeAOR: {
          maop: this.controllerNote()?.maop,
          falseAlarm: this.controllerNote()?.falseAlarm,
          abnormalOperation: this.controllerNote()?.abnormalOperation,
          includeInTurnover: this.controllerNote()?.addToTurnover,
          calloutInitiated: this.controllerNote()?.calloutInitiated,
          maintenance: this.controllerNote()?.maintenance,
        },
      });
    }
  }

  onCategoryChange($event: any) {
    this.category.set($event.value.name);
    this.updateFormValidations();
    this.updateNoteTypes();
    this.filterFacilities();
  }

  filterFacilities() {
    let filterTypes: string[] = [];
    switch (this.category()) {
      case 'Compressor Station':
        filterTypes = ['Compressor'];
        this.facilities = this.aorFilteredFacilities.filter((facility) =>
          filterTypes.includes(facility.facilityType!)
        );
        break;
      case 'M&R':
        filterTypes = [
          'Measurement',
          'Regulation',
          'Compressor',
          'Odorizer',
          'Storage',
        ];
        this.facilities = this.aorFilteredFacilities.filter((facility) =>
          filterTypes.includes(facility.facilityType!)
        );
        break;
      case 'Valve':
        filterTypes = ['Valve', 'Control Valve', 'Mainline Valve', 'MLV'];
        this.facilities = [
          ...this.aorFilteredFacilities,
          ...this.aorHiddenFacilities,
        ];
        this.facilities = this.facilities.filter((facility) =>
          filterTypes.includes(facility.facilityType!)
        );
        break;
      default:
        this.facilities = this.aorFilteredFacilities;
        break;
    }

    // if we have state/county/township selections we should only show facilities that match
    const selectedState = this.noteFormGroup
      ?.get('contactLocInfo')
      ?.get('state')?.value?.name;
    const selectedCounty = this.noteFormGroup
      ?.get('contactLocInfo')
      ?.get('county')?.value?.name;
    const selectedTownship = this.noteFormGroup
      ?.get('contactLocInfo')
      ?.get('township')?.value?.name;
    if (selectedState) {
      this.facilities = this.facilities.filter((facility) => {
        return facility.locations.some(
          (location: Location) => location.state === selectedState
        );
      });
    }
    if (selectedCounty) {
      this.facilities = this.facilities.filter((facility) => {
        return facility.locations.some(
          (location: Location) => location.county === selectedCounty
        );
      });
    }
    if (selectedTownship) {
      this.facilities = this.facilities.filter((facility) => {
        return facility.locations.some(
          (location: Location) => location.township === selectedTownship
        );
      });
    }

    if (this.controllerNote()?.AORId) {
      this.facilities = this.facilities.filter((facility) => {
        return facility?.AORs?.map((aor) => aor.id).includes(
          this.controllerNote()?.AORId!
        );
      });
    }

    this.facilityIds = [...this.facilities];
    this.facilityIds = this.facilityIds.sort((a, b) =>
      sortByProperty(a, b, 'facilityUid')
    );
  }

  filterLocations(): void {
    const selectedState = this.noteFormGroup.get('contactLocInfo')?.get('state')
      ?.value?.name;
    const selectedCounty = this.noteFormGroup
      ?.get('contactLocInfo')
      ?.get('county')?.value?.name;
    const selectedTownship = this.noteFormGroup
      ?.get('contactLocInfo')
      ?.get('township')?.value?.name;
    let filteredLocs: Location[] = [...this.totalLocations];
    if (selectedState) {
      filteredLocs = filteredLocs.filter((loc) => loc.state === selectedState);
    }
    if (selectedCounty) {
      filteredLocs = filteredLocs.filter(
        (loc) => loc.county === selectedCounty
      );
    }
    if (selectedTownship) {
      filteredLocs = filteredLocs.filter(
        (loc) => loc.township === selectedTownship
      );
    }

    this.states = [];
    this.counties = [];
    this.townships = [];
    filteredLocs.forEach((loc) => {
      if (!this.states.some((state) => state.name === loc.state)) {
        this.states.push({ name: loc.state });
      }
      if (!this.counties.some((county) => county.name === loc.county)) {
        this.counties.push({ name: loc.county });
      }
      if (!this.townships.some((township) => township.name === loc.township)) {
        this.townships.push({ name: loc.township });
      }
    });
    this.states.sort(sortByName);
    this.counties.sort(sortByName);
    this.townships.sort(sortByName);

    // Now we have filtered our locations dropdowns, we should also filter the facility dropdowns to match
    this.filterFacilities();
  }

  haveWeLoadedEverything() {
    return this.totalNoteTypes.length > 0;
  }

  updateFormValidations() {
    // Clear validations on all controls under the contactLocInfo sub form group
    // these are the fields that change depending on noteType
    let controls = (this.noteFormGroup.get('contactLocInfo') as FormGroup)
      .controls;
    for (const key in controls) {
      this.noteFormGroup.get('contactLocInfo')?.get(key)?.clearValidators();
      this.noteFormGroup
        .get('contactLocInfo')
        ?.get(key)
        ?.updateValueAndValidity();
    }
    switch (this.category()) {
      // Compressor Station, M&R, and Valve Require facility and facilityId, Valve also requires Valve Position
      case 'Compressor Station':
      case 'M&R':
      case 'Valve':
        let facilityName = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('facility');
        facilityName?.setValidators([Validators.required]);
        facilityName?.updateValueAndValidity();
        let facilityId = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('facilityId');
        facilityId?.setValidators([Validators.required]);
        facilityId?.updateValueAndValidity();
        if (this.category() === 'Valve') {
          let valvePosition = this.noteFormGroup
            .get('contactLocInfo')
            ?.get('valvePosition');
          valvePosition?.setValidators([Validators.required]);
          valvePosition?.updateValueAndValidity();
        }
        break;

      // Pipeline Requires pipelineNum
      case 'Pipeline':
        let pipelineNum = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('pipelineNum');
        pipelineNum?.setValidators([Validators.required]);
        pipelineNum?.updateValueAndValidity();
        break;

      // External Caller Requires none of the bottom half of the form
      case 'External Caller':
        let controls = (this.noteFormGroup.get('contactLocInfo') as FormGroup)
          .controls;
        for (const key in controls) {
          this.noteFormGroup.get('contactLocInfo')?.get(key)?.clearValidators();
          this.noteFormGroup
            .get('contactLocInfo')
            ?.get(key)
            ?.updateValueAndValidity();
        }
        break;

      // System requires only systemName
      case 'System':
        let systemName2 = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('systemName');
        systemName2?.setValidators(Validators.required);
        systemName2?.updateValueAndValidity;
        break;
    }
  }

  disableForm() {
    this.noteFormGroup?.get('typeAOR')?.disable();
    this.noteFormGroup?.get('contactLocInfo')?.disable();
  }

  enableForm() {
    this.noteFormGroup?.get('typeAOR')?.enable();
    this.noteFormGroup?.get('contactLocInfo')?.enable();
  }

  disableFormControlsForEditMode() {
    let category = this.noteFormGroup?.get('typeAOR')?.get('category');
    category?.disable();
    // this.noteFormGroup?.get('typeAOR')?.get('createdAt')?.disable();
    switch (this.category()) {
      case 'External Caller':
        let state = this.noteFormGroup.get('contactLocInfo')?.get('state');
        state?.disable();
        let county = this.noteFormGroup.get('contactLocInfo')?.get('county');
        county?.disable();
        let township = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('township');
        township?.disable();
        break;
      case 'System':
        let systemName = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('systemName');
        systemName?.disable();
        break;
    }
  }

  enableFormControlsForCreateMode() {
    let category = this.noteFormGroup.get('typeAOR')?.get('category');
    category?.enable();
    let noteType, facilityName, facilityId;
    noteType = this.noteFormGroup.get('typeAOR')?.get('noteType');
    noteType?.enable();
    switch (this.category()) {
      case 'Compressor Station':
      case 'M&R':
      case 'Valve':
        facilityName = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('facility');
        facilityName?.enable();
        facilityId = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('facilityId');
        facilityId?.enable();
        break;
      case 'Pipeline':
        break;
      case 'External Caller':
        facilityName = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('facility');
        facilityName?.enable();
        facilityId = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('facilityId');
        facilityId?.enable();
        let state = this.noteFormGroup.get('contactLocInfo')?.get('state');
        state?.enable();
        let county = this.noteFormGroup.get('contactLocInfo')?.get('county');
        county?.enable();
        let township = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('township');
        township?.enable();
        break;
      case 'System':
        let systemName = this.noteFormGroup
          .get('contactLocInfo')
          ?.get('systemName');
        systemName?.enable();
        break;
    }
  }

  updateNoteTypes() {
    switch (this.category()) {
      case 'Compressor Station':
        this.noteTypes = this.totalNoteTypes.filter((type) =>
          type.categories.some((cat) => cat.name == 'Compressor Station')
        );
        break;
      case 'M&R':
        this.noteTypes = this.totalNoteTypes.filter((type) =>
          type.categories.some((cat) => cat.name == 'M&R')
        );
        break;
      case 'Pipeline':
        this.noteTypes = this.totalNoteTypes.filter((type) =>
          type.categories.some((cat) => cat.name == 'Pipeline')
        );
        break;
      case 'External Caller':
        this.noteTypes = this.totalNoteTypes.filter((type) =>
          type.categories.some((cat) => cat.name == 'External Caller')
        );
        break;
      case 'System':
        this.noteTypes = this.totalNoteTypes.filter((type) =>
          type.categories.some((cat) => cat.name == 'System')
        );
        break;
      case 'Valve':
        this.noteTypes = this.totalNoteTypes.filter((type) =>
          type.categories.some((cat) => cat.name == 'Valve')
        );
        break;
      default:
        this.noteTypes = [];
        break;
    }

    // Filter on AOR as well
    setTimeout(() => {
      const aorField = this.noteFormGroup.get('typeAOR')?.get('aor');
      const currentAOR = aorField?.value;
      this.noteTypes = this.noteTypes.filter((type) =>
        type.aors?.some((aor) => aor.id === currentAOR?.id)
      );
    }, 1);
  }

  prepareForm() {
    if (this.haveWeLoadedEverything()) {
      this.updateNoteTypes();
      this.updateFields();
      this.filterFacilities();
      this.updateFormValidations();
      if (this.formType() === 'Create') {
        this.enableFormControlsForCreateMode();
      } else {
        this.disableForm();
      }
      this.isLoading = false;
    }
  }

  onEditEntry($event: any) {
    this.selectedEntry = $event;
    this.openEntryForm(this.selectedEntry, 'Edit');
  }

  onEntryDeleted($event: string) {
    this.entries = this.entries?.filter((x) => x.id !== $event)!;
    this.entriesLoading = false;
  }

  onEntryCopied($event: Entry) {
    this.entries?.push($event);
    this.entriesLoading = false;
  }

  onDeleteStart() {
    this.entriesLoading = true;
  }

  getLocationFromForm() {
    const location = [
      this.noteFormGroup.controls['contactLocInfo'].get('state')?.value?.name ??
        '',
      this.noteFormGroup.controls['contactLocInfo'].get('township')?.value
        ?.name ?? '',
      this.noteFormGroup.controls['contactLocInfo'].get('county')?.value
        ?.name ?? '',
    ];

    return location;
  }

  onFacilityChange($event: any) {
    if (this.category() === 'Pipeline') {
      return; // Don't auto populate other fields on pipelines
    }
    this.selectedFacility = $event.value;
    const oldLocation = this.getLocationFromForm();
    let state = '',
      county = '',
      township = '';
    if (this.selectedFacility?.locations.length ?? 0 > 0) {
      let firstLocation = this.selectedFacility!.locations[0];
      state = firstLocation.state;
      county = firstLocation.county;
      township = firstLocation.township;
    }
    this.noteFormGroup.patchValue({
      contactLocInfo: {
        facilityId: this.selectedFacility,
        facilityScadaId: this.selectedFacility,
        state: this.states.find((x) => x.name === state),
        county: this.counties.find((x) => x.name === county),
        township: this.townships.find((x) => x.name === township),
      },
    });

    const newLocation = [state, township, county];

    if ($event.value === null || oldLocation != newLocation) {
      // Reload Facility options, since the location has changed.
      this.loadFacilities();
    }

    if (!this.selectedFacility) {
      this.showActiveFacilityNotes = false;
      this.showActiveVisitors = false;
    } else {
      this.checkFacilityNotes(this.selectedFacility.id);
      this.checkForVisitors(this.selectedFacility.id);
    }
  }

  onFacilityIdChange($event: any) {
    if (this.category() === 'Pipeline') {
      return; // Don't auto populate other fields on pipelines
    }

    this.selectedFacility = $event.value;
    const oldLocation = this.getLocationFromForm();
    let state = '',
      county = '',
      township = '';
    if (this.selectedFacility?.locations.length ?? 0 > 0) {
      let firstLocation = this.selectedFacility!.locations[0];
      state = firstLocation.state;
      county = firstLocation.county;
      township = firstLocation.township;
    }
    this.noteFormGroup.patchValue({
      contactLocInfo: {
        facility: this.selectedFacility,
        facilityScadaId: this.selectedFacility,
        state: this.states.find((x) => x.name === state),
        county: this.counties.find((x) => x.name === county),
        township: this.townships.find((x) => x.name === township),
      },
    });

    const newLocation = [state, township, county];

    if ($event.value === null || oldLocation != newLocation) {
      // Reload Facility options, since the location has changed.
      this.loadFacilities();
    }

    if (!this.selectedFacility) {
      this.showActiveFacilityNotes = false;
      this.showActiveVisitors = false;
    } else {
      this.checkFacilityNotes(this.selectedFacility.id);
      this.checkForVisitors(this.selectedFacility.id);
    }
  }

  onScadaIdChange($event: any) {
    if (this.category() === 'Pipeline') {
      return; // Don't auto populate other fields on pipelines
    }

    this.selectedFacility = $event.value;
    const oldLocation = this.getLocationFromForm();
    let state = '',
      county = '',
      township = '';
    if (this.selectedFacility?.locations.length ?? 0 > 0) {
      let firstLocation = this.selectedFacility!.locations[0];
      state = firstLocation.state;
      county = firstLocation.county;
      township = firstLocation.township;
    }
    this.noteFormGroup.patchValue({
      contactLocInfo: {
        facility: this.selectedFacility,
        facilityId: this.selectedFacility,
        state: this.states.find((x) => x.name === state),
        county: this.counties.find((x) => x.name === county),
        township: this.townships.find((x) => x.name === township),
      },
    });

    const newLocation = [state, township, county];

    if ($event.value === null || oldLocation != newLocation) {
      // Reload Facility options, since the location has changed.
      this.loadFacilities();
    }

    if (!this.selectedFacility) {
      this.showActiveFacilityNotes = false;
    } else {
      this.checkFacilityNotes(this.selectedFacility.id);
      this.checkForVisitors(this.selectedFacility.id);
    }
  }

  getScadaIdOptions() {
    if (this.facilityIds) {
      return this.facilityIds.filter((f) => f.scadaId);
    }

    return [];
  }

  checkFormReady() {
    // There are 5 data sets (excluding facilities) that must load before the form is ready.
    // Facilities load independently and the form is usable without them.
    if (this.completedDataLoads != 5) {
      return;
    }
    this.prepareForm();
  }

  /**
   * Called when facilities finish loading. Patches form values for Edit mode
   * and enables the facility dropdown controls.
   */
  onFacilitiesLoaded(): void {
    if (this.formType() === 'Edit' && this.controllerNote()) {
      this.noteFormGroup.patchValue({
        contactLocInfo: {
          facility: this.facilities.find(
            (facility) =>
              facility.facilityName === this.controllerNote()?.facilityName
          ),
          facilityId: this.facilities.find(
            (facility) =>
              facility.facilityUid === this.controllerNote()?.facilityUID
          ),
          facilityScadaId: this.facilities.find(
            (facility) =>
              facility.facilityUid === this.controllerNote()?.facilityUID
          ),
        },
      });
    }
    // Re-run validation now that facilities are available and form values are patched
    this.updateFormValidations();
  }

  checkFacilityNotes(selectedFacilityId: any): void {
    if (!selectedFacilityId) {
      console.log(
        'No Selected facility ID available, skipping facility note load.'
      );
      return;
    }

    this._facilityAPI.getFacility(selectedFacilityId).subscribe({
      next: (facility) => {
        this.showActiveFacilityNotes = false;
        this.facility.set(facility);
        this.facilityNotepadEntries.set(
          this.facility()?.NotepadEntries?.filter((entry) => !entry.archived) ??
            []
        );
        if (
          this.facilityNotepadEntries()?.length > 0 ||
          (this.facility()?.notes?.length ?? 0) > 0
        ) {
          this.showActiveFacilityNotes = true;
        }
      },
      error: (error) => {
        selectedFacilityId = null;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'COMMON.MESSAGES.ERROR.FACILITY_INFO',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
    });
  }

  async openFacilityNotesDialog(): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get(
        'LOCATION.MESSAGES.HEADERS.VIEW_FACILITY_NOTES'
      )
    );
    this.ref = this._dialogService.open(FacilityNotesDialogComponent, {
      header: dialogHeader,
      width: '60%',
      modal: true,
      dismissableMask: true,
      closable: true,
      data: {
        facility: this.facility(),
        facilityNotepadEntries: this.facilityNotepadEntries(),
      },
    });
    this.ref?.onClose.subscribe((data) => {
      return;
    });
  }

  async openMusterListDialog(): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get(
        'OPMONITORING.MESSAGES.HEADERS.VIEW_MUSTER_LIST'
      )
    );
    this.ref = this._dialogService.open(FacilityVisitorLogComponent, {
      header: dialogHeader,
      width: '50%',
      modal: true,
      dismissableMask: true,
      closable: true,
      data: {
        facilityId: this.facilityId(),
      },
    });
    this.ref?.onClose.subscribe((data) => {
      return;
    });
  }

  checkForVisitors(selectedFacilityId: string | null): void {
    this.facilityId.set(selectedFacilityId);
    this.showActiveVisitors = true;
  }

  async onActionSelected(action: string) {
    if (
      action === 'Edit Note' &&
      (this.canEdit || (this.canEditOwn && this.isOwnNote()))
    ) {
      this.isEditing = true;
      this.enableForm();
      this.disableFormControlsForEditMode();
    } else if (action === 'Email Note') {
      this.openEmailDialog();
    } else if (action === 'View Audit Trail') {
      this.openAuditTrailDialog();
    } else if (action === 'Print Note') {
      printJS({
        printable: 'printContainer',
        style: '#printContainer { display: block; height: 100%; }',
        type: 'html',
      });
    } else if (action === 'Delete Note') {
      const message = await firstValueFrom(
        this._translateService.get('CRM.MESSAGES.CONFIRMATION.DELETE_NOTE')
      );
      this._confirmationService.confirm({
        message: message,
        key: 'main-note-delete',
        accept: () => {
          this.isLoading = true;
          this._controllerNoteApi
            .deleteControllerNote(this.controllerNote()?.id ?? '')
            .subscribe({
              next: (result) => {
                this._logAndMessage.translateToSuccessMessage({
                  headerKey: 'COMMON.MESSAGES.HEADERS.DELETE',
                  bodyKey: 'CRM.MESSAGES.SUCCESS.DELETE_CONTROLLER_NOTE',
                });
                this.noteFormRef?.close();
              },
              error: (error) => {
                console.error(error);
                this._logAndMessage.translateToErrorAlert({
                  headerKey: 'COMMON.MESSAGES.HEADERS.DELETE_ERROR',
                  bodyKey: 'COMMON.MESSAGES.ERROR.DELETE_ERROR_MSG',
                });
                this.isLoading = false;
              },
            });
        },
      });
    }

    if (!this.isEditing) {
      setTimeout(() => {
        this.actionsDropdownChild().clear();
      }, 100);
    }
  }

  async fillActionsDropdown() {
    this.availableActions = [];
    const labels = await firstValueFrom(
      forkJoin({
        edit: this._translateService.get('CRM.LABEL.EDIT_NOTE'),
        delete: this._translateService.get('CRM.LABEL.DELETE_NOTE'),
        print: this._translateService.get('CRM.LABEL.PRINT_NOTE'),
        email: this._translateService.get('CRM.LABEL.EMAIL_NOTE'),
        audit: this._translateService.get('COMMON.LABEL.VIEW_AUDIT_INFO'),
      })
    );

    if (this.formType() !== 'read-only') {
      if (this.canEdit || (this.canEditOwn && this.isOwnNote())) {
        this.availableActions.push({ label: labels.edit, value: EDIT_NOTE });
      }

      if (this.canDelete || (this.canDeleteOwn && this.isOwnNote())) {
        this.availableActions.push({
          label: labels.delete,
          value: DELETE_NOTE,
        });
      }
    }

    // always show print email or audit trail,
    // because if they can make it to this page, they have perms
    this.availableActions.push({ label: labels.print, value: PRINT_NOTE });
    this.availableActions.push({ label: labels.email, value: EMAIL_NOTE });
    this.availableActions.push({
      label: labels.audit,
      value: VIEW_AUDIT,
    });
  }

  isOwnNote(): boolean {
    return this._auth.isOwnItem(this.controllerNote()?.createdBy);
  }

  openAuditTrailDialog(): void {
    const history$ = this._controllerNoteApi
      .getControllerNotesHistories(this.controllerNote()!.id)
      .pipe(take(1));
    this.ref = this._dialogService.open(AuditDialogContainerComponent, {
      dismissableMask: true,
      modal: true,
      closable: true,
      width: '25%',
      data: {
        history: history$,
      },
    });
  }

  async openEmailDialog(): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get('CRM.MESSAGES.HEADERS.EMAIL_NOTE')
    );

    this.ref = this._dialogService.open(EmailDialogContainerComponent, {
      header: dialogHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      data: {},
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        return;
      }
      this.emailControllerNote(data);
    });
  }

  emailControllerNote($event: any) {
    if (!this.controllerNote()?.id) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'CRM.MESSAGES.ERROR.CONTROLLER_NOTE_NOT_SELECTED',
      });

      return;
    }

    const cEventData = $event;

    let sBCCList = cEventData.bccList;
    let sEmailNotes = cEventData.sEmailNotes;

    if (sBCCList === null || typeof sBCCList === 'undefined') {
      sBCCList = '';
    }

    if (sEmailNotes === null || typeof sEmailNotes === 'undefined') {
      sEmailNotes = '';
    }

    const req = this._adminBuilder.buildEmailRequest(
      this.controllerNote()!.id,
      cEventData.toList,
      sBCCList,
      sEmailNotes
    );

    this._controllerNoteApi.emailControllerNote(req).subscribe({
      next: (result) => {
        this._logAndMessage.translateToSuccessMessage({
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          bodyKey: 'CRM.MESSAGES.SUCCESS.EMAIL_CONTROLLER_NOTE',
        });
      },
      error: (error) => {
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CRM.MESSAGES.ERROR.EMAIL_CONTROLLER_NOTE',
        });
      },
    });
  }

  getControllerNote(): void {
    this._controllerNoteApi.getControllerNote(this.id()!, false).subscribe({
      next: async (note: ControllerNote): Promise<any> => {
        this.controllerNote.set(note);
        this.category.set(this.controllerNote()?.category!);
        this.updateFields();
        this.isLoading = false;

        if (!this._deviceService.isMobile()) {
          const label = await firstValueFrom(
            this._translateService.get('CRM.LABEL.NOTE_VIEW')
          );
          this._tabService.updateActiveTabLabel(label);
        }
      },
      error: (error) => console.error(error),
    });
  }

  getSCADAControlValues() {
    this._controllerNoteApi.getSCADAControlValues().subscribe({
      next: (SCADAControlValues: SCADAControlValue[]) => {
        let sortedList = SCADAControlValues.sort((a, b) => sortByName(a, b));
        this.SCADAControlValues.set(sortedList);
      },
      error: (error) => {
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CRM.MESSAGES.ERROR.RETRIEVE_SCADA_CONTROL_VALUES',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
    });
  }

  dialogHidden(): void {
    this.displayEmailDialog = false;
  }

  ngOnDestroy(): void {
    if (this.ref) {
      this.isEditing = false;
      this.disableForm();
      this.ref.close();
    }
  }

  canEditEntry(entry: Entry) {
    return (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .EDIT_CONTROLLER_NOTE_ENTRIES,
      ]) ||
      (this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .EDIT_OWN_CONTROLLER_NOTE_ENTIRES,
      ]) &&
        entry.createdBy === this._auth.getUserName()) ||
      (this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .EDIT_OWN_CONTROLLER_NOTE_ENTIRES,
      ]) &&
        this._auth.getEmail()!.startsWith(entry.createdBy!))
    );
  }

  canDeleteEntry(entry: Entry) {
    return (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .DELETE_CONTROLLER_NOTE_ENTIRES,
      ]) ||
      (this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .DELETE_OWN_CONTROLLER_NOTE_ENTRIES,
      ]) &&
        entry.createdBy === this._auth.getUserName()) ||
      (this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.ENTRIES
          .DELETE_OWN_CONTROLLER_NOTE_ENTRIES,
      ]) &&
        this._auth.getEmail()!.startsWith(entry.createdBy!))
    );
  }

  async deleteEntry(entry: Entry) {
    const message = await firstValueFrom(
      this._translateService.get('CRM.MESSAGES.CONFIRMATION.DELETE_NOTE_ENTRY')
    );
    this._confirmationService.confirm({
      message: message,
      accept: () => {
        this.isLoading = true;
        this._controllerNoteApi.deleteNoteEntry(entry.id ?? '').subscribe({
          next: () => {
            this.onEntryDeleted(entry.id ?? '');
            this.isLoading = false;
          },
          error: (error) => console.error(error),
        });
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }
}
